/**
 * CaseList.jsx
 *
 * All Cases page using the AE Tracker design system.
 * Layout: page-head, access-banner, cases-layout row with filter panel + table panel.
 */

import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { getCases, getTags, getStats, prefetchDashboardSummary } from '../api/client.js';
import { FIRST_BATCH } from '../lib/caseLoadPolicy.js';
import { tagColourClass } from '../utils/tagColors.js';
import { buildSidebarTags } from '../utils/tagFilter.js';
import { useAuth } from '../auth/AuthContext.jsx';
import DateInput from '../components/DateInput.jsx';
import { PillSet } from '../components/Pills.jsx';
import {
  parseCaseListParams,
  buildCaseListParams,
  INACTIVE_DAY_OPTIONS,
} from '../utils/urlState.js';
import { eventCategoryOf } from '@shared/eventCategory.js';
import { REFUSAL_REGISTER_TAG } from '@shared/refusalRegister.js';
import useUrlViewSync from '../hooks/useUrlViewSync.js';

import SavedViews from '../components/SavedViews.jsx';
import StatCard from '../components/StatCard.jsx';
import KpiGrid from '../components/KpiGrid.jsx';
import StatusBadge, { SeverityBadge, TreatmentBadge } from '../components/StatusBadge.jsx';
import CostChip from '../components/CostChip.jsx';
import { buildSlaCell, slaSortKey, daysSince, formatAgo } from '../lib/sla.js';
import { formatDateTime } from '../lib/dates.js';
import { clipText } from '../lib/clipText.js';
import { DEFAULT_HIDDEN_COLS, ALL_COLS, withUnseenOptInsHidden } from '../lib/caseListColumns.js';
import ShareLinkButton from '../components/ShareLinkButton.jsx';
import { useAppConfig } from '../contexts/AppConfigContext.jsx';

function formatDate(dt) {
  if (!dt) return '—';
  const d = new Date(dt);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// Role → access banner text
function accessMessage(role) {
  switch (role) {
    case 'SuperAdmin': return 'You are viewing as Super Admin. Scope: all cases · all comments · admin panel · full cost edit.';
    case 'Supervisor': return 'You are viewing as Supervisor. Scope: all cases · cost feature · private + public comments.';
    case 'SupervisorView': return 'You are viewing as Supervisor (view). Scope: all cases · cost view · read-only.';
    case 'Admin': return 'You are viewing as Administrator. Scope: all cases · all comments (private included).';
    case 'HoRO': return 'You are viewing as Head of Regional Operations. Scope: all cases in your region · all comments.';
    case 'ClinicManager': return 'You are viewing as Clinic Manager. Scope: cases for your clinic · public comments only.';
    case 'ReadOnly': return 'You have read-only access. You can view cases but cannot post comments or create new submissions.';
    default: return `You are viewing as ${role}.`;
  }
}

// A search starting with a digit is a case-ID lookup — prepend "ae-" so "728"
// finds AE-728 (and ranks it as an exact ref match) instead of any ref/clinic
// that merely contains "728".
function toRefQuery(q) {
  return /^\d/.test(q) ? `ae-${q}` : q;
}

// True if a case matches every search word (across ref / title / clinic /
// region / staff member). Shared by the main filter and the "you might be
// looking for" fallback so the two never diverge.
function caseStaffName(c) {
  return (c._raw?.StaffMemberConcerned || c._raw?.StaffMember || '').toLowerCase();
}

// The Incident Category cell/sort/search value: the free text when the category
// is "Other" (the word alone says nothing), else the category itself.
const INCIDENT_CATEGORY_CLIP = 30;   // chars, the "..." included
function caseIncidentCategory(c) {
  const cat = (c.incidentCategory || '').trim();
  const other = (c.incidentCategoryOther || '').trim();
  if (cat === 'Other' && other) return { text: other, isOther: true };
  return { text: cat, isOther: false };
}

function caseMatchesSearch(c, tokens) {
  if (!tokens.length) return true;
  const ref    = (c.caseRef || '').toLowerCase();
  const title  = (c.title   || '').toLowerCase();
  const clinic = (c.clinic  || '').toLowerCase();
  const region = (c.region  || '').toLowerCase();
  const staff  = caseStaffName(c);
  const cat    = caseIncidentCategory(c).text.toLowerCase();
  return tokens.every(t =>
    ref.includes(toRefQuery(t)) || title.includes(t) || clinic.includes(t) || region.includes(t) || staff.includes(t)
    || cat.includes(t));
}

// Build the pagination items: always show first + last page, a sliding window
// of pages around the current one, and a 'gap' marker (rendered as …) wherever
// the sequence jumps. Returns e.g. [1, 'gap', 8, 9, 10, 11, 12, 'gap', 20].
function getPageItems(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const pages = new Set([1, total, current, current - 1, current + 1]);
  const sorted = [...pages].filter(p => p >= 1 && p <= total).sort((a, b) => a - b);
  const items = [];
  let prev = 0;
  for (const p of sorted) {
    if (p - prev > 1) items.push('gap');
    items.push(p);
    prev = p;
  }
  return items;
}

// Categorise a case for the Event filter group — now `@shared/eventCategory.js`,
// so the /api/stats/by-clinic rollup (which Thérapie Maps' clinic card links
// into this list from) buckets cases by exactly the rule this filter uses.

// Country filter: `key` is the legacy internal name (UK/IE), `label` is what the
// user sees (GB/IOI), `country` is the cases.country value it matches. US added
// for the US clinics. Single source for the checkboxes, counts, and match logic.
const COUNTRY_FILTERS = [
  { key: 'UK', label: 'GB',  country: 'United Kingdom' },
  { key: 'IE', label: 'IOI', country: 'Ireland' },
  { key: 'US', label: 'US',  country: 'United States' },
];
const EMPTY_COUNTRY_FILTER = Object.freeze({ UK: false, IE: false, US: false });
const anyCountryActive = (fc) => COUNTRY_FILTERS.some(f => fc[f.key]);
function matchesCountryFilter(fc, country) {
  const active = COUNTRY_FILTERS.filter(f => fc[f.key]);
  return !active.length || active.some(f => f.country === country);
}

// ── Column chooser ───────────────────────────────────────────────────────────
// Which optional columns the user has switched OFF (Case ID is always shown).
// Stored as the hidden set so a column added later defaults to visible — except
// the opt-in ones (SLA, Incident Category), which start hidden even for a stored
// choice that predates them (see lib/caseListColumns.js).
const HIDDEN_COLS_KEY = 'ae:caseList:hiddenColumns';
const KNOWN_COLS_KEY  = 'ae:caseList:knownColumns';
function loadHiddenCols() {
  try {
    const raw = localStorage.getItem(HIDDEN_COLS_KEY);
    if (raw == null) return new Set(DEFAULT_HIDDEN_COLS);
    const v = JSON.parse(raw);
    if (!Array.isArray(v)) return new Set(DEFAULT_HIDDEN_COLS);
    const known = JSON.parse(localStorage.getItem(KNOWN_COLS_KEY) || 'null');
    return withUnseenOptInsHidden(v.filter(x => typeof x === 'string'), known);
  } catch { return new Set(DEFAULT_HIDDEN_COLS); }
}
function saveHiddenCols(set) {
  // Always written — an empty list means "the user chose to show everything",
  // which must survive a reload rather than fall back to the defaults.
  try {
    localStorage.setItem(HIDDEN_COLS_KEY, JSON.stringify([...set]));
    localStorage.setItem(KNOWN_COLS_KEY, JSON.stringify(ALL_COLS));
  } catch { /* storage unavailable — the choice lives for this session only */ }
}

// Fallback row height for the "one page of rows" floor when no real row is
// rendered yet (the fitter measures real rows whenever it can).
const FALLBACK_ROW_PX = 57;

const COLUMN_HINTS = {
  lastActivity: 'How long since anything happened on the case — a comment, upload, edit or status change. Notification sends don’t count.',
  sla:          'Response SLA: whose turn it is and when the response is due. Sort to bring the most urgent to the top.',
};

// Sidebar groups fold in this order when the panel runs out of room and unfold
// in reverse once there is space again. Tags and Clinic (pill lists) give way
// first. Every group can be folded by hand; Inactive for and Date range are one
// pill each, so the fitter leaves them alone.
const AUTO_COLLAPSE_ORDER = ['tags', 'clinic', 'country', 'severity', 'event', 'status'];
const FOLD_HYSTERESIS_PX = 8;

// "3h ago" for today, then days, months, years — coarse on purpose: the column
// answers "is anyone looking at this?", not "when exactly".
function agoLabel(at, nowMs) {
  const d = daysSince(at, nowMs);
  if (d == null) return '—';
  if (d < 1)   return formatAgo(at, nowMs);          // "just now" · "12m ago" · "5h ago"
  if (d < 60)  return `${d}d ago`;
  if (d < 365) return `${Math.floor(d / 30)}mo ago`;
  const y = Math.floor(d / 365), mo = Math.floor((d % 365) / 30);
  return mo ? `${y}y ${mo}mo ago` : `${y}y ago`;
}

function Chevron({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="6 9 12 15 18 9"/>
    </svg>
  );
}

/** A sidebar filter group with a fold-away body. `activeCount` shows on the
 *  folded header so a hidden group can never conceal an active filter. */
function FilterGroup({ id, title, hint, collapsed, onToggle, onClear, onAdd, activeCount = 0, refFn, children, foldable = true }) {
  const isCollapsed = foldable && collapsed;
  return (
    <div className={`filter-group${isCollapsed ? ' collapsed' : ''}${foldable ? '' : ' static'}`} ref={refFn}>
      <div className="filter-group-title">
        <span className="fg-lead">
          {foldable ? (
            <button type="button" className="fg-toggle" aria-expanded={!isCollapsed} aria-controls={`fg-${id}`} title={hint} onClick={onToggle}>
              <Chevron className="fg-chev" />
              {title}
              {isCollapsed && activeCount > 0 && <span className="fg-active" title={`${activeCount} active`}>{activeCount}</span>}
            </button>
          ) : (
            <span className="fg-static" title={hint}>{title}</span>
          )}
          {onAdd && (
            <button type="button" className="fg-add" aria-label={`Pick ${title.toLowerCase()}`} title={`Pick ${title.toLowerCase()}…`} onClick={onAdd}>
              <svg viewBox="0 0 12 12" width="9" height="9" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
                <path d="M6 1.5v9M1.5 6h9" />
              </svg>
            </button>
          )}
        </span>
        {onClear && <span className="clear" onClick={onClear}>Clear</span>}
      </div>
      <div className="filter-group-body" id={`fg-${id}`} hidden={isCollapsed}>{children}</div>
    </div>
  );
}

/**
 * Table-with-plus icon (Marius's pick). The plus turns teal while there are
 * hidden columns that could be added; with every column in view the whole
 * glyph sits in the button's grey.
 */
function ColumnsIcon({ canAdd }) {
  return (
    <svg className="col-glyph" viewBox="0 0 24 24" width="16" height="16" style={{ width: 16, height: 16 }}
      fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21.187 5.99805C20.9854 5.10929 20.6539 4.43424 20.1089 3.88929C18.7177 2.49805 16.4785 2.49805 12.0002 2.49805C7.52184 2.49805 5.28267 2.49805 3.89143 3.88929C2.50018 5.28053 2.50018 7.5197 2.50018 11.998C2.50018 16.4764 2.50018 18.7156 3.89143 20.1068C5.28267 21.498 7.52184 21.498 12.0002 21.498C16.4785 21.498 18.7177 21.498 20.1089 20.1068C20.6539 19.5619 20.9854 18.8868 21.187 17.998" />
      <path d="M9 21.5L9 2.5" />
      <path d="M2.5 15.5L9 15.5M2.5 8.5L9 8.5" />
      <path d="M21.5 12L17.5 12M17.5 12H13.5M17.5 12V16M17.5 12L17.5 8" stroke={canAdd ? 'var(--brand)' : 'currentColor'} strokeWidth={canAdd ? 1.9 : 1.5} />
    </svg>
  );
}

/** − / ● / + for one tag: exclude, ignore, require. */
function TagTriState({ label, mode, onChange }) {
  const q = `“${label}”`;
  const tip = {
    not: mode === 'not' ? `Excluding cases tagged ${q} — click to clear` : `Exclude cases tagged ${q}`,
    off: mode ? `Stop filtering on ${q}` : `${q} is not filtering`,
    has: mode === 'has' ? `Only cases tagged ${q} — click to clear` : `Only cases tagged ${q}`,
  };
  return (
    <span className="tri" role="group" aria-label={`${label}: exclude, off or has`}>
      <button type="button" className={`tri-btn not${mode === 'not' ? ' on' : ''}`} aria-pressed={mode === 'not'}
        title={tip.not} onClick={() => onChange(mode === 'not' ? null : 'not')}>−</button>
      <button type="button" className={`tri-btn off${!mode ? ' on' : ''}`} aria-pressed={!mode}
        title={tip.off} onClick={() => onChange(null)}><span className="dot" aria-hidden="true" /></button>
      <button type="button" className={`tri-btn has${mode === 'has' ? ' on' : ''}`} aria-pressed={mode === 'has'}
        title={tip.has} onClick={() => onChange(mode === 'has' ? null : 'has')}>+</button>
    </span>
  );
}

/** Centred dialog for a filter picker: title, body, Cancel / OK. Escape or the backdrop cancel. */
function PickerModal({ title, children, onOk, onCancel, okDisabled = false, width = 460 }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onCancel(); } };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);
  return createPortal(
    <div className="pick-backdrop" onMouseDown={onCancel}>
      <div className="pick-modal" role="dialog" aria-modal="true" aria-label={title} style={{ width }} onMouseDown={e => e.stopPropagation()}>
        <div className="pick-head">
          <h3>{title}</h3>
          <button type="button" className="pick-close" aria-label="Close" onClick={onCancel}>×</button>
        </div>
        <div className="pick-body">{children}</div>
        <div className="pick-foot">
          <button type="button" className="btn sm ghost" onClick={onCancel}>Cancel</button>
          <button type="button" className="btn sm primary" onClick={onOk} disabled={okDisabled}>OK</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Clinics: search + tick list; OK commits the draft. */
function ClinicPickerModal({ clinics, value, onOk, onCancel }) {
  const [draft, setDraft] = useState(() => new Set(value));
  const [q, setQ] = useState('');
  const needle = q.trim().toLowerCase();
  const rows = needle ? clinics.filter(c => c.name.toLowerCase().includes(needle)) : clinics;
  const toggle = (name) => setDraft(prev => { const n = new Set(prev); if (n.has(name)) n.delete(name); else n.add(name); return n; });
  return (
    <PickerModal title="Clinics" onOk={() => onOk(draft)} onCancel={onCancel}>
      <input className="input ss-search" placeholder="Search clinics…" value={q} onChange={e => setQ(e.target.value)} autoFocus />
      <div className="pick-list">
        {rows.map(c => (
          <label key={c.name} className="filter-option" title={c.name}>
            <input type="checkbox" checked={draft.has(c.name)} onChange={() => toggle(c.name)} />
            {c.name.replace(/^Therapie Clinic\s*/i, '') || c.name}
            <span className="count-chip">{c.count}</span>
          </label>
        ))}
        {rows.length === 0 && <div className="tag-pop-empty">No clinics match “{q.trim()}”</div>}
      </div>
      <div className="pick-summary">
        <span>{draft.size ? `${draft.size} selected` : 'All clinics'}</span>
        {draft.size > 0 && <button type="button" onClick={() => setDraft(new Set())}>Clear</button>}
      </div>
    </PickerModal>
  );
}

/** Tags: search + a − ● + control per tag; OK commits the draft. */
function TagPickerModal({ tags, value, onOk, onCancel }) {
  const [draft, setDraft] = useState(() => ({ ...value }));
  const [q, setQ] = useState('');
  const needle = q.trim().toLowerCase();
  const rows = needle
    ? tags.filter(t => t.label.toLowerCase().includes(needle) || t.slug.toLowerCase().includes(needle))
    : tags;
  const set = (slug, mode) => setDraft(prev => { const n = { ...prev }; if (mode) n[slug] = mode; else delete n[slug]; return n; });
  const active = Object.values(draft).filter(Boolean).length;
  return (
    <PickerModal title="Tags" onOk={() => onOk(draft)} onCancel={onCancel}>
      <input className="input ss-search" placeholder="Search tags…" value={q} onChange={e => setQ(e.target.value)} autoFocus />
      <div className="tag-pop-head">
        <span>Tag</span>
        <span className="tri-head"><span title="Exclude cases with the tag">− not</span><span title="Not filtering on the tag"><span className="dot" /></span><span title="Only cases with the tag">+ has</span></span>
      </div>
      <div className="pick-list tag-pop-list">
        {rows.map(t => (
          <div key={t.slug} className={`tag-pop-row${draft[t.slug] ? ' on' : ''}${t.count === 0 ? ' zero' : ''}`}>
            <span className="lbl">
              <span className={t.swatchClass || 'tag-swatch none'} aria-hidden="true" />
              <span className="txt">{t.label}</span>
              <span className="n">{t.count}</span>
            </span>
            <TagTriState label={t.label} mode={draft[t.slug]} onChange={(m) => set(t.slug, m)} />
          </div>
        ))}
        {rows.length === 0 && <div className="tag-pop-empty">No tags match “{q.trim()}”</div>}
      </div>
      <div className="pick-summary">
        <span>{active ? `${active} active` : 'Nothing selected'}</span>
        {active > 0 && <button type="button" onClick={() => setDraft({})}>Clear</button>}
      </div>
    </PickerModal>
  );
}

/** Countries: tick list with counts; OK commits the draft. */
function CountryPickerModal({ options, value, onOk, onCancel }) {
  const [draft, setDraft] = useState(() => ({ ...value }));
  const picked = Object.values(draft).filter(Boolean).length;
  return (
    <PickerModal title="Countries" width={360} onOk={() => onOk(draft)} onCancel={onCancel}>
      <div className="pick-list">
        {options.map(o => (
          <label key={o.key} className="filter-option">
            <input type="checkbox" checked={!!draft[o.key]} onChange={() => setDraft(prev => ({ ...prev, [o.key]: !prev[o.key] }))} />
            {o.label}
            <span className="count-chip">{o.count}</span>
          </label>
        ))}
      </div>
      <div className="pick-summary">
        <span>{picked ? `${picked} selected` : 'All countries'}</span>
        {picked > 0 && <button type="button" onClick={() => setDraft({ ...EMPTY_COUNTRY_FILTER })}>Clear</button>}
      </div>
    </PickerModal>
  );
}

/** Inactive for: one choice. */
function InactivePickerModal({ value, onOk, onCancel }) {
  const [draft, setDraft] = useState(value);
  return (
    <PickerModal title="Inactive for" width={360} onOk={() => onOk(draft)} onCancel={onCancel}>
      <p className="pick-hint">Only cases with no comment, upload, edit or status change for at least this long.</p>
      <div className="pick-list">
        {[0, ...INACTIVE_DAY_OPTIONS].map(d => (
          <label key={d} className="filter-option">
            <input type="radio" name="inactiveDaysPick" checked={draft === d} onChange={() => setDraft(d)} />
            {d === 0 ? 'Any' : `${d}+ days`}
          </label>
        ))}
      </div>
    </PickerModal>
  );
}

const DATE_RANGE_PRESETS = ['All time', 'Last 7 days', 'Last 30 days', 'Last quarter', 'Last year', 'Custom range'];

/** Date reported: a preset, or a custom From / To. */
function DateRangePickerModal({ value, onOk, onCancel }) {
  const [range, setRange] = useState(value.range);
  const [from, setFrom] = useState(value.from || '');
  const [to, setTo] = useState(value.to || '');
  return (
    <PickerModal title="Date reported" width={380} onOk={() => onOk({ range, from, to })} onCancel={onCancel}>
      <div className="pick-list">
        {DATE_RANGE_PRESETS.map(pr => (
          <label key={pr} className="filter-option">
            <input type="radio" name="dateRangePick" checked={range === pr} onChange={() => setRange(pr)} />
            {pr}
          </label>
        ))}
      </div>
      {range === 'Custom range' && (
        <div className="pick-dates">
          <label style={{ display: 'block' }}>
            <div className="label-sm">From</div>
            <DateInput value={from} max={to || undefined} onChange={e => setFrom(e.target.value)} />
          </label>
          <label style={{ display: 'block' }}>
            <div className="label-sm">To</div>
            <DateInput value={to} min={from || undefined} onChange={e => setTo(e.target.value)} />
          </label>
        </div>
      )}
    </PickerModal>
  );
}

/** Pill text for the Date reported filter. */
function dateRangeChipLabel(range, from, to) {
  if (range !== 'Custom range') return range;
  if (from && to) return `${formatDate(from)} – ${formatDate(to)}`;
  if (from) return `From ${formatDate(from)}`;
  if (to) return `Until ${formatDate(to)}`;
  return 'Custom range';
}

/** Icon-only "Pick columns in view" button; opens the tick list. Case ID is fixed. */
function ColumnPicker({ columns, hidden, onToggle, onReset }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const ref = useRef(null);
  const menuRef = useRef(null);
  // The menu is portalled to <body> (the panel clips overflow) and pinned under
  // the button's right edge; it follows the button on scroll/resize.
  useEffect(() => {
    if (!open) return;
    const place = () => {
      const r = ref.current?.getBoundingClientRect();
      // clientWidth, not innerWidth: fixed positioning ignores the page scrollbar.
      if (r) setPos({ top: r.bottom + 6, right: Math.max(8, document.documentElement.clientWidth - r.right) });
    };
    place();
    const onDoc = (e) => {
      if (ref.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);
  const total = columns.length + 1;            // + the fixed Case ID column
  const shown = total - hidden.size;
  const label = hidden.size > 0 ? `Pick columns in view · ${shown} of ${total} shown` : 'Pick columns in view';
  return (
    <span className="col-picker" ref={ref}>
      <button
        type="button"
        className="btn sm col-picker-btn"
        aria-haspopup="true" aria-expanded={open} aria-label={label} title={label}
        onClick={() => setOpen(o => !o)}
      >
        <ColumnsIcon canAdd={hidden.size > 0} />
      </button>
      {open && pos && createPortal(
        <div className="col-menu dd-shell dd-right" role="group" aria-label="Visible columns" ref={menuRef} style={{ top: pos.top, right: pos.right }}>
          <label className="filter-option is-inert"><input type="checkbox" checked readOnly disabled /> Case ID</label>
          {columns.map(col => (
            <label className="filter-option" key={col.field}>
              <input type="checkbox" checked={!hidden.has(col.field)} onChange={() => onToggle(col.field)} />
              {col.label}
            </label>
          ))}
          <div className="col-menu-foot">
            <span>{shown} of {total} shown</span>
            <button type="button" onClick={onReset} disabled={hidden.size === 0}>Show all</button>
          </div>
        </div>,
        document.body,
      )}
    </span>
  );
}

export default function CaseList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { config } = useAppConfig();
  const canSubmit = !!user?.canSubmitCase;
  const canSeeCosts = !!user?.canSeeCosts;
  const slaOn = config.slaEnabled === true;

  // Table columns after the fixed Case ID cell. Order here = order on screen.
  const columnDefs = useMemo(() => [
    { field: 'clinic',       label: 'Clinic' },
    { field: 'treatment',    label: 'Event' },
    { field: 'severity',     label: 'Severity' },
    { field: 'status',       label: 'Status' },
    { field: 'staffMember',  label: 'Staff Member' },
    { field: 'incidentCategory', label: 'Incident Category' },
    { field: 'reportedDate', label: 'Reported' },
    { field: 'lastActivity', label: 'Last activity' },
    ...(slaOn ? [{ field: 'sla', label: 'SLA' }] : []),
    ...(canSeeCosts ? [{ field: 'cost', label: 'Cost' }] : []),
  ], [slaOn, canSeeCosts]);
  const [hiddenCols, setHiddenCols] = useState(loadHiddenCols);
  const visibleCols = columnDefs.filter(c => !hiddenCols.has(c.field));

  // ── State ─────────────────────────────────────────────────────────────────
  const [cases, setCases] = useState([]);
  const [stats, setStats] = useState(null);
  const [loadingCases, setLoadingCases] = useState(true);
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingRest, setLoadingRest]   = useState(false);
  const [casesError, setCasesError] = useState(null);
  // Background-phase failure: first batch rendered fine but the rest didn't
  // arrive. Non-fatal — table stays up, KPIs stay loading (partial totals lie).
  const [restError, setRestError] = useState(null);

  // Filter state
  const [searchText, setSearchText] = useState('');

  // Side panel filter state
  const [filterStatuses, setFilterStatuses] = useState({
    new: true,
    open: true,
    pending: true,
    claims: true,
    closed: false,
  });
  const [filterTreatments, setFilterTreatments] = useState({
    lhr: true,
    awi: true,
    other: true,
    nearMiss: true,
    clinicIncident: true,
  });
  const [filterSeverities, setFilterSeverities] = useState({
    'Code 1': false,
    'Code 2': false,
    'Code 3': false,
    'Code 4': false,
    'Code 5': false,
    '(none)': false,   // cases with no severity assigned
  });
  const [filterCountries, setFilterCountries] = useState({ ...EMPTY_COUNTRY_FILTER });
  const [filterClinics, setFilterClinics] = useState(new Set());
  const [tagStates, setTagStates] = useState({});           // slug → 'has' | 'not' (tri-state tag filter)
  const [tagVocab, setTagVocab] = useState([]);              // full tag vocabulary (GET /api/tags)
  const [tagVocabError, setTagVocabError] = useState(false); // GET /api/tags failed → fall back to tags seen on loaded cases
  const [dateRange, setDateRange] = useState('All time');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [inactiveDays, setInactiveDays] = useState(0);   // "Inactive for" — 0 = any
  const [picker, setPicker] = useState(null);            // 'clinic' | 'tags' | 'inactive' | 'date' | null

  // Sidebar groups. `autoCollapsed` is what the fitter folded; `manual` is what
  // the user did ({ mode: 'open' | 'closed', t }). A group the user opened stays
  // open and the fitter folds OTHER groups to make room — it only takes a pinned
  // group back (oldest first) when nothing else is left. A group the user closed
  // stays closed.
  const [manual, setManual] = useState({});
  const [autoCollapsed, setAutoCollapsed] = useState(() => new Set());
  const [, bumpSidebar] = useState(0);
  // Floor for the cases row (px): the filters' fully-open height, capped at one
  // page of rows — measured, so a full page never gets padding under it.
  const [casesMinH, setCasesMinH] = useState(0);
  const filterBodyRef = useRef(null);
  const casesPanelRef = useRef(null);
  const groupRefs = useRef({});
  const groupNaturalH = useRef({});
  const isGroupCollapsed = (id) => (manual[id] ? manual[id].mode === 'closed' : autoCollapsed.has(id));
  const toggleGroup = (id) => {
    const opening = isGroupCollapsed(id);
    setManual(prev => ({ ...prev, [id]: { mode: opening ? 'open' : 'closed', t: Date.now() } }));
    if (opening) setAutoCollapsed(prev => { if (!prev.has(id)) return prev; const n = new Set(prev); n.delete(id); return n; });
  };
  const groupRef = (id) => (el) => { groupRefs.current[id] = el; };

  // Counts per Event filter option (LHR / AWI / Other / Near miss).
  const eventCounts = { lhr: 0, awi: 0, other: 0, nearMiss: 0, clinicIncident: 0 };
  for (const c of cases) { const cat = eventCategoryOf(c); if (cat) eventCounts[cat]++; }

  // Build clinic list from cases — country filter scopes the list when active.
  const sidebarClinics = (() => {
    const map = {};
    for (const c of cases) {
      if (!c.clinic) continue;
      if (!matchesCountryFilter(filterCountries, c.country)) continue;
      if (!map[c.clinic]) map[c.clinic] = { name: c.clinic, count: 0 };
      map[c.clinic].count++;
    }
    return Object.values(map).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  })();

  // Sidebar tag filter: list the FULL vocabulary (so ANY tag is selectable, even
  // one not on the loaded page), overlaid with per-tag counts from the loaded set
  // (count 0 when no loaded case carries it). If the vocabulary fetch failed,
  // buildSidebarTags still surfaces any tag seen on a loaded case, so the filter
  // degrades gracefully rather than disappearing.
  const sidebarTags = buildSidebarTags(tagVocab, cases);

  // How much each checkbox group is narrowing — shown on a folded header.
  const countOn = (flags) => Object.values(flags).filter(Boolean).length;
  const groupActive = {
    status:   countOn(filterStatuses)   < Object.keys(filterStatuses).length   ? countOn(filterStatuses)   : 0,
    event:    countOn(filterTreatments) < Object.keys(filterTreatments).length ? countOn(filterTreatments) : 0,
    severity: countOn(filterSeverities),
    country:  countOn(filterCountries),
  };
  const activeTagCount = Object.values(tagStates).filter(Boolean).length;

  // Sort state: { col: 'clinic'|'reportedDate'|'caseRef'|'severity', dir: 'asc'|'desc' }
  const [sort, setSort] = useState(() => config.defaultSort);

  function toggleSort(col) {
    setSort(prev =>
      prev.col === col
        ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { col, dir: 'asc' }
    );
    setPage(1);
  }

  function sortIndicator(col) {
    if (sort.col !== col) return <span className="sort-ind inactive" aria-hidden="true">↕</span>;
    return <span className="sort-ind">{sort.dir === 'asc' ? '↑' : '↓'}</span>;
  }

  function toggleColumn(field) {
    const hiding = !hiddenCols.has(field);
    // Keep one column besides Case ID. Count only columns on screen — the hidden
    // set also carries opt-ins that aren't offered right now (SLA while it's off).
    const hiddenOnScreen = columnDefs.filter(c => hiddenCols.has(c.field)).length;
    if (hiding && hiddenOnScreen >= columnDefs.length - 1) return;
    const next = new Set(hiddenCols);
    if (hiding) next.add(field); else next.delete(field);
    saveHiddenCols(next);
    setHiddenCols(next);
    if (hiding && sort.col === field) setSort(config.defaultSort);      // hiding the sorted column → default order
  }
  function resetColumns() { saveHiddenCols(new Set()); setHiddenCols(new Set()); }

  // Pagination
  const [page, setPage] = useState(1);
  const PAGE_SIZE = config.defaultPageSize;

  // KPI row collapse — reclaim vertical space for the table. Persisted per user.
  const [statsCollapsed, setStatsCollapsed] = useState(
    () => localStorage.getItem('ae:caseList:statsCollapsed') === '1'
  );
  const toggleStats = useCallback(() => {
    setStatsCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('ae:caseList:statsCollapsed', next ? '1' : '0');
      return next;
    });
  }, []);

  // ── Data loading ───────────────────────────────────────────────────────────
  // SuperAdmin bin view — show soft-deleted cases (90-day restore window)
  const [showDeleted, setShowDeleted] = useState(false);
  // SuperAdmin test view — ONLY cases filed against a Thérapie Maps TEST clinic,
  // a separate list like the Deleted bin (filters set aside, Selected / All are
  // the way back out). OFF by default so the list and the dashboard show the
  // same number of cases; the server ignores the flag for everyone else, so this
  // chip is a convenience, never the thing keeping test cases out of anyone's list.
  const [showTest, setShowTest] = useState(false);
  // "All" view: every live case, the sidebar filters set aside (but kept).
  const [viewAll, setViewAll] = useState(false);
  const applyingViewRef = useRef(false);

  const applyCaseListView = useCallback((view) => {
    applyingViewRef.current = true;   // a URL-driven filter change must not kick us out of All
    setFilterStatuses(view.filterStatuses);
    setFilterTreatments(view.filterTreatments);
    setFilterSeverities(view.filterSeverities);
    setFilterCountries(view.filterCountries);
    setFilterClinics(new Set(view.filterClinics));
    setTagStates({ ...(view.tagStates || {}) });
    setSearchText(view.searchText);
    setDateRange(view.dateRange);
    setCustomDateFrom(view.customDateFrom);
    setCustomDateTo(view.customDateTo);
    setPage(view.page);
    setSort(view.sort);
    setShowDeleted(view.showDeleted);
    setInactiveDays(view.inactiveDays ?? 0);
    setViewAll(!!view.viewAll);
  }, []);

  useUrlViewSync({
    parse: sp => parseCaseListParams(sp, config),
    serialize: () => buildCaseListParams({
      filterStatuses,
      filterTreatments,
      filterSeverities,
      filterCountries,
      filterClinics,
      tagStates,
      searchText,
      dateRange,
      customDateFrom,
      customDateTo,
      page,
      sort,
      showDeleted,
      inactiveDays,
      viewAll,
    }, config),
    apply: applyCaseListView,
    deps: [
      filterStatuses, filterTreatments, filterSeverities, filterCountries,
      filterClinics, tagStates, searchText, dateRange, customDateFrom, customDateTo,
      page, sort, showDeleted, inactiveDays, viewAll, config.defaultSort,
    ],
  });

  // ── Saved views (per-user) ─────────────────────────────────────────────────
  // A view = filters + columns + sort. Clinic users see only their own clinic, so
  // the feature (and its welcome card) is hidden for them.
  const isClinicUser = user?.role === 'ClinicManager' || user?.role === 'ClinicMailbox';
  // The view's filter/sort signature, with page neutralised (paging is not part of
  // a "view", so it never lights the star or gets saved).
  const viewParamsStr = useMemo(() => buildCaseListParams({
    filterStatuses, filterTreatments, filterSeverities, filterCountries, filterClinics,
    tagStates, searchText, dateRange, customDateFrom, customDateTo, page: 1, sort,
    showDeleted, inactiveDays, viewAll,
  }, config).toString(), [filterStatuses, filterTreatments, filterSeverities, filterCountries,
    filterClinics, tagStates, searchText, dateRange, customDateFrom, customDateTo, sort,
    showDeleted, inactiveDays, viewAll, config]);
  // The star means "you've changed something since this view was loaded" — not
  // "differs from the code default". Column choices persist per browser, so a user
  // who once hid columns must not see a permanent star. Baseline = the state as
  // loaded (re-baselined when a saved view is applied or the current one is saved).
  const viewSig = `${viewParamsStr}||${[...hiddenCols].slice().sort().join(',')}`;
  const viewSigRef = useRef(viewSig); viewSigRef.current = viewSig;
  const [baselineSig, setBaselineSig] = useState(viewSig);
  const rebaseRef = useRef(false);
  useEffect(() => {
    if (rebaseRef.current) { rebaseRef.current = false; setBaselineSig(viewSigRef.current); }
  }, [viewSig]);
  const isViewCustomised = viewSig !== baselineSig;

  const getCurrentView = useCallback(() => ({ params: viewParamsStr, hiddenCols: [...hiddenCols], knownCols: [...ALL_COLS] }), [viewParamsStr, hiddenCols]);
  const applySavedView = useCallback((view) => {
    if (!view) return;
    applyCaseListView(parseCaseListParams(new URLSearchParams(view.params || ''), config));
    const cols = Array.isArray(view.hiddenCols)
      ? withUnseenOptInsHidden(view.hiddenCols, view.knownCols)   // a view saved before a column existed
      : new Set(DEFAULT_HIDDEN_COLS);
    setHiddenCols(cols); saveHiddenCols(cols);
    rebaseRef.current = true;   // once the applied state settles, treat it as the new baseline
  }, [applyCaseListView, config]);
  const resetToDefaultView = useCallback(() => {
    applyCaseListView(parseCaseListParams(new URLSearchParams(), config));   // app-default filters + sort
    const cols = new Set(DEFAULT_HIDDEN_COLS);
    setHiddenCols(cols); saveHiddenCols(cols);
    rebaseRef.current = true;
  }, [applyCaseListView, config]);
  const markViewSaved = useCallback(() => setBaselineSig(viewSigRef.current), []);

  // Welcome card — introduce saved views once, non-clinic only, and only until the
  // user has saved their first view.
  const [savedViewsCount, setSavedViewsCount] = useState(0);
  const VIEWS_WELCOME_KEY = 'ae:caseList:savedViewsWelcome';
  const [welcomeDismissed, setWelcomeDismissed] = useState(() => {
    try { return localStorage.getItem(VIEWS_WELCOME_KEY) === 'dismissed'; } catch { return false; }
  });
  const dismissViewsWelcome = () => {
    setWelcomeDismissed(true);
    try { localStorage.setItem(VIEWS_WELCOME_KEY, 'dismissed'); } catch { /* session only */ }
  };
  const showViewsWelcome = !isClinicUser && !welcomeDismissed && savedViewsCount === 0;

  // Scope-aware filter groups: a Country / Clinic filter that can't narrow anything
  // is clutter. Hide both for the clinic-side roles (CM / clinic mailbox), and hide
  // either once the user's own cases only ever carry a single value for it — e.g.
  // Country for a single-country country/area manager (custom roles, so keyed on
  // what they can actually see, not the role name).
  // Clinic-scoped = the built-in clinic roles OR any (custom, e.g. Area Manager) role
  // the server resolved to a fixed set of clinics — those users navigate one small
  // patch, so Country and Clinic filters are noise for them.
  const clinicScoped = isClinicUser || (Array.isArray(user?.clinicEmails) && user.clinicEmails.length > 0);
  const casesReady = !loadingCases && cases.length > 0;
  const scopedCountryCount = useMemo(() => new Set(cases.map(c => String(c.country || '').trim()).filter(Boolean)).size, [cases]);
  const scopedClinicCount = useMemo(() => new Set(cases.map(c => String(c.clinic || '').trim()).filter(Boolean)).size, [cases]);
  const showCountryGroup = config.visibleFilterGroups.country && !clinicScoped && !(casesReady && scopedCountryCount <= 1);
  const showClinicGroup  = config.visibleFilterGroups.clinic  && !clinicScoped && !(casesReady && scopedClinicCount  <= 1);

  // Two-phase load: fetch the first FIRST_BATCH rows (API order = reportedDate
  // desc = default sort, so they're exactly the first screens), render the
  // table immediately, then pull the remaining rows in the background.
  const loadSeq = useRef(0);
  const [searchQuery, setSearchQuery] = useState('');

  const loadCases = useCallback(async () => {
    const seq = ++loadSeq.current;   // invalidates in-flight loads on re-trigger
    try {
      setLoadingCases(true);
      setLoadingStats(true);
      setLoadingRest(false);
      setCasesError(null);
      setRestError(null);
      const filters = { list: true, ...(showDeleted ? { deleted: true } : {}), ...(showTest ? { test: true } : {}) };

      // KPI row: all-time server aggregates (stable while the table loads in phases).
      const statsPromise = getStats()
        .then(s => {
          if (seq !== loadSeq.current) return;
          setStats({
            new: s.new ?? 0,
            open: s.open ?? 0,
            pending: s.pending ?? 0,
            withClaims: s.withClaims ?? 0,
            closed: s.closed ?? 0,
            total: s.total ?? 0,
          });
          setLoadingStats(false);
        })
        .catch(() => { /* KPIs stay empty on failure */ });

      const [first] = await Promise.all([
        getCases({ ...filters, limit: FIRST_BATCH }),
        statsPromise,
      ]);
      if (seq !== loadSeq.current) return;
      setCases(Array.isArray(first) ? first : []);
      setLoadingCases(false);

      if (Array.isArray(first) && first.length === FIRST_BATCH) {
        setLoadingRest(true);
        try {
          const rest = await getCases({ ...filters, offset: FIRST_BATCH });
          if (seq !== loadSeq.current) return;
          setCases(prev => {
            const seen = new Set(prev.map(c => c.id));
            return [...prev, ...rest.filter(c => !seen.has(c.id))];
          });
        } catch (err) {
          if (seq === loadSeq.current) setRestError(err.message);
        } finally {
          if (seq === loadSeq.current) setLoadingRest(false);
        }
      }
    } catch (err) {
      if (seq === loadSeq.current) {
        setCasesError(err.message);
        setLoadingCases(false);
        setLoadingStats(false);
        setLoadingRest(false);
      }
    }
  }, [showDeleted, showTest, user?.email, user?.role, user?.impersonating]);

  const loadSearch = useCallback(async (query) => {
    const seq = ++loadSeq.current;
    try {
      setLoadingCases(true);
      setLoadingRest(false);
      setCasesError(null);
      setRestError(null);
      const filters = { list: true, search: query, ...(showDeleted ? { deleted: true } : {}), ...(showTest ? { test: true } : {}) };
      const results = await getCases(filters);
      if (seq !== loadSeq.current) return;
      setCases(Array.isArray(results) ? results : []);
    } catch (err) {
      if (seq === loadSeq.current) {
        setCasesError(err.message);
      }
    } finally {
      if (seq === loadSeq.current) setLoadingCases(false);
    }
  }, [showDeleted, showTest]);

  // Debounce search → server query (all-time; no hot-window cap).
  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(searchText.trim()), 300);
    return () => clearTimeout(t);
  }, [searchText]);

  useEffect(() => {
    if (searchQuery) loadSearch(searchQuery);
    else loadCases();
  }, [searchQuery, loadCases, loadSearch]);

  // Warm dashboard summary while the user browses cases.
  useEffect(() => {
    if (!loadingCases && !searchQuery) {
      prefetchDashboardSummary().catch(() => {});
    }
  }, [loadingCases, searchQuery]);

  // Load the FULL tag vocabulary so the sidebar filter lists EVERY tag — not just
  // the ones on the loaded page. Counts (below) come from the loaded set; a tag
  // with no loaded cases shows a 0 count. On failure we DON'T swallow the error:
  // a small inline signal renders in the Tags group and buildSidebarTags falls
  // back to tags seen on the loaded cases, so the filter stays usable. `reloadTagVocab`
  // is exposed for a Retry link. `bumpTagVocab` re-runs the effect.
  const [tagVocabReload, bumpTagVocab] = useState(0);
  const reloadTagVocab = useCallback(() => bumpTagVocab(n => n + 1), []);
  useEffect(() => {
    let alive = true;
    setTagVocabError(false);
    getTags()
      .then(v => { if (alive) setTagVocab(Array.isArray(v) ? v : []); })
      .catch(() => { if (alive) setTagVocabError(true); });
    return () => { alive = false; };
  }, [tagVocabReload]);

  // Every refusal-register entry is born Closed, and Closed is off by default —
  // so filtering on the RefusalRegister tag would otherwise show an empty register.
  // Picking that tag switches Closed on: visibly (the checkbox ticks) and
  // reversibly. Spec: refusal_register_logic.md.
  useEffect(() => {
    if (tagStates[REFUSAL_REGISTER_TAG.slug] !== 'has') return;
    setFilterStatuses(prev => (prev.closed ? prev : { ...prev, closed: true }));
  }, [tagStates]);

  // ── Sidebar height = table height ─────────────────────────────────────────
  // CSS pins the filter panel to its grid cell, so it is always exactly as tall
  // as the cases panel. This pass makes the groups FIT that height — never a
  // scrollbar, never idle space a folded group could use:
  //   • too tall → fold groups in AUTO_COLLAPSE_ORDER (skipping ones the user
  //     opened) until it fits; if that isn't enough, take back user-opened groups
  //     oldest first (the one opened last is the one they want to see);
  //   • room to spare → unfold auto-folded groups, most-used first, but only the
  //     ones that actually fit (a small group can take space a big one can't).
  // Heights come from the DOM: expanded groups are measured each pass and
  // remembered, so a folded group's size is known when deciding to reopen it.
  useEffect(() => {
    const body = filterBodyRef.current;
    if (!body || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => bumpSidebar(t => t + 1));
    ro.observe(body);
    return () => ro.disconnect();
  }, []);
  // Safety valve: the fitter is a no-deps layout effect that converges in a
  // handful of passes; if something ever makes it oscillate, stop touching
  // state for a moment instead of hitting React's update-depth limit.
  const fitPasses = useRef(0);
  const fitResetTimer = useRef(null);
  useLayoutEffect(() => {
    const body = filterBodyRef.current;
    const content = body?.firstElementChild;
    if (!body || !content || loadingCases) return;
    fitPasses.current += 1;
    if (!fitResetTimer.current) {
      fitResetTimer.current = setTimeout(() => { fitPasses.current = 0; fitResetTimer.current = null; }, 400);
    }
    if (fitPasses.current > 24) {
      if (fitPasses.current === 25) console.warn('[CaseList] sidebar fitter did not settle — pausing');
      return;
    }
    if (window.matchMedia('(max-width: 1100px)').matches) {   // stacked layout: nothing to fit
      if (autoCollapsed.size) setAutoCollapsed(new Set());
      if (casesMinH) setCasesMinH(0);
      return;
    }
    // How much a group's body adds when open (its title stays either way).
    const bodyHeight = (id) => {
      const el = groupRefs.current[id];
      if (!el) return null;
      const titleH = el.firstElementChild?.offsetHeight || 0;
      if (!isGroupCollapsed(id)) groupNaturalH.current[id] = el.offsetHeight;
      const natural = groupNaturalH.current[id];
      return natural == null ? null : Math.max(0, natural - titleH);
    };
    for (const id of AUTO_COLLAPSE_ORDER) bodyHeight(id);   // refresh the cache for open groups
    const cs = getComputedStyle(body);
    const padY = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);

    // ── Floor: min(filters fully open, one page of rows) ──────────────────
    // Every group counts: foldable ones at their open height, static ones as is.
    const present = Object.entries(groupRefs.current).filter(([, el]) => el);
    const gap = parseFloat(getComputedStyle(content).rowGap) || 0;
    const headH = body.previousElementSibling?.offsetHeight || 0;
    const sidebarOpen = headH + padY
      + present.reduce((sum, [id, el]) => sum + (groupNaturalH.current[id] ?? el.offsetHeight), 0)
      + gap * Math.max(0, present.length - 1);
    const panel = casesPanelRef.current;
    let onePage = Infinity;
    if (panel) {
      const rows = [...panel.querySelectorAll('tbody tr')].filter(r => !r.querySelector('td[colspan]'));
      const rowH = rows.length
        ? rows.map(r => r.offsetHeight).sort((a, b) => a - b)[Math.floor(rows.length / 2)]
        : FALLBACK_ROW_PX;
      const chrome = (panel.querySelector('.filters')?.offsetHeight || 0)
        + (panel.querySelector('thead')?.offsetHeight || 0)
        + (panel.querySelector('.pagination')?.offsetHeight || 0);
      onePage = chrome + PAGE_SIZE * rowH;
    }
    const floor = Math.round(Math.min(sidebarOpen, onePage));
    if (Number.isFinite(floor) && Math.abs(floor - casesMinH) > 2) {
      setCasesMinH(floor);   // re-run on the next pass with the new height
      return;
    }

    // ── Fit the groups into the height we have ────────────────────────────
    const used = content.offsetHeight + padY;
    let free = body.clientHeight - used;

    const nextAuto = new Set(autoCollapsed);
    let nextManual = manual;
    if (free < 0) {
      for (const id of AUTO_COLLAPSE_ORDER) {
        if (free >= 0) break;
        if (!groupRefs.current[id] || isGroupCollapsed(id) || manual[id]?.mode === 'open') continue;
        const d = bodyHeight(id);
        if (d == null) continue;
        nextAuto.add(id); free += d;
      }
      if (free < 0) {
        // Take back user-opened groups oldest first — but never the one opened
        // last: that is the one being looked at. If it alone doesn't fit, the
        // body scrolls; folding it would make the group unreachable.
        const pinned = AUTO_COLLAPSE_ORDER
          .filter(id => groupRefs.current[id] && manual[id]?.mode === 'open' && !isGroupCollapsed(id))
          .sort((a, b) => manual[a].t - manual[b].t);
        const newest = pinned[pinned.length - 1];
        for (const id of pinned) {
          if (free >= 0) break;
          if (id === newest) continue;
          const d = bodyHeight(id);
          if (d == null) continue;
          if (nextManual === manual) nextManual = { ...manual };
          delete nextManual[id];
          nextAuto.add(id); free += d;
        }
      }
    } else {
      for (const id of [...AUTO_COLLAPSE_ORDER].reverse()) {
        if (!nextAuto.has(id) || manual[id]) continue;
        const d = bodyHeight(id);
        if (d == null) continue;
        if (d + FOLD_HYSTERESIS_PX <= free) { nextAuto.delete(id); free -= d; }
      }
    }
    const autoChanged = nextAuto.size !== autoCollapsed.size || [...nextAuto].some(id => !autoCollapsed.has(id));
    if (autoChanged) setAutoCollapsed(nextAuto);
    if (nextManual !== manual) setManual(nextManual);
  });

  // ── Client-side filtering ─────────────────────────────────────────────────
  // Tokenised search: split on spaces and require EVERY word to match somewhere
  // (so "bath 3" → Bath cases whose ref contains AE-3). A numeric word is
  // treated as a case-ID fragment via toRefQuery.
  const searchRaw    = searchText.trim().toLowerCase();
  const searchTokens = searchRaw ? searchRaw.split(/\s+/) : [];
  const nowMs = Date.now();
  // Newest human touch on the case (events + comments minus notification noise,
  // computed by the API); falls back to the record's own timestamps.
  const lastActivityOf = (c) => c.lastActivityAt || c.modifiedDate || c.reportedDate || null;

  const passesSearch = (c) => !!searchQuery || caseMatchesSearch(c, searchTokens);
  const hasTags = new Set(Object.keys(tagStates).filter(k => tagStates[k] === 'has'));
  const notTags = new Set(Object.keys(tagStates).filter(k => tagStates[k] === 'not'));
  // The sidebar filters. They define the "Selected" view and nothing else.
  const passesFilters = (c) => {
    // Status checkboxes
    const st = c.status;
    if (st === 'New') {
      if (!filterStatuses.new) return false;
    } else if (st === 'Open') {
      if (!filterStatuses.open) return false;
    } else if (st === 'Pending') {
      if (!filterStatuses.pending) return false;
    } else if (st === 'With Claims') {
      if (!filterStatuses.claims) return false;
    } else if (st === 'Closed') {
      if (!filterStatuses.closed) return false;
    } else {
      // Status without its own toggle (e.g. the retired Awaiting Client Input,
      // or a custom status added before code catches up): only show it when no
      // status narrowing is active, so it can never leak into a filtered list.
      if (!Object.values(filterStatuses).every(Boolean)) return false;
    }

    // Event / treatment checkboxes — categorise once (near-miss matches on event
    // type, the rest on treatment) and gate on the matching filter.
    const cat = eventCategoryOf(c);
    if (cat && !filterTreatments[cat]) return false;

    // Severity — if any checked, restrict to those codes
    const anySevChecked = Object.values(filterSeverities).some(Boolean);
    if (anySevChecked) {
      // No-code cases are only shown when the "No code" chip is checked.
      if (c.severity) { if (!filterSeverities[c.severity]) return false; }
      else            { if (!filterSeverities['(none)'])   return false; }
    }

    // Country filter
    if (!matchesCountryFilter(filterCountries, c.country)) return false;

    // Clinic filter
    if (filterClinics.size > 0 && !filterClinics.has(c.clinic)) return false;

    // Tag filter — tri-state per tag: the case must carry NONE of the "not"
    // tags and, if any "has" tags are set, at least one of them.
    if (hasTags.size || notTags.size) {
      const slugs = (c.tags || []).map(t => t.slug);
      if (notTags.size && slugs.some(sl => notTags.has(sl))) return false;
      if (hasTags.size && !slugs.some(sl => hasTags.has(sl))) return false;
    }

    // Date range filter
    if (dateRange !== 'All time') {
      const dt = c.reportedDate || c.createdDate;
      if (!dt) return false;
      const reported = new Date(dt);
      if (dateRange === 'Custom range') {
        if (customDateFrom) {
          const from = new Date(customDateFrom);
          from.setHours(0, 0, 0, 0);
          if (reported < from) return false;
        }
        if (customDateTo) {
          const to = new Date(customDateTo);
          to.setHours(23, 59, 59, 999);
          if (reported > to) return false;
        }
      } else {
        const now = new Date();
        const daysMap = { 'Last 7 days': 7, 'Last 30 days': 30, 'Last quarter': 90, 'Last year': 365 };
        const days = daysMap[dateRange];
        if (days && (now - reported) > days * 86400000) return false;
      }
    }

    // Inactive for — stale-case triage: nothing human has happened for N+ days.
    if (inactiveDays > 0) {
      const idle = daysSince(lastActivityOf(c), nowMs);
      if (idle == null || idle < inactiveDays) return false;
    }

    return true;
  };
  // Selected = the filters apply. All and the bin show every loaded case; the
  // search box applies in every view.
  const sideView = showDeleted || showTest;   // the bin / test list: only those rows are loaded
  const filteredCases = cases.filter(c => passesSearch(c) && (sideView || viewAll || passesFilters(c)));

  // Touching any filter while in All means "show me what that selects".
  const filtersSig = JSON.stringify([
    filterStatuses, filterTreatments, filterSeverities, filterCountries,
    [...filterClinics], tagStates, dateRange, customDateFrom, customDateTo, inactiveDays,
  ]);
  const filtersSigRef = useRef(filtersSig);
  useEffect(() => {
    if (filtersSig === filtersSigRef.current) return;
    filtersSigRef.current = filtersSig;
    if (applyingViewRef.current) return;         // came from the URL, not the user
    if (viewAll) setViewAll(false);
  }, [filtersSig, viewAll]);
  useEffect(() => { applyingViewRef.current = false; });

  // Sort
  const SEVERITY_ORDER = { 'Code 5': 0, 'Code 4': 1, 'Code 3': 2, 'Code 2': 3, 'Code 1': 4, '': 5, null: 5 };
  const costTotal = (c) =>
    c?.costs ? Object.values(c.costs).reduce((s, v) => s + (Number(v) || 0), 0) : 0;

  // Rank by match quality so an exact case-ref hit sits on top, then the closest
  // word matches. The whole query is checked against the ref first (so "3" → AE-3
  // is exact); otherwise a numeric word that names the ref pulls that case up
  // within the multi-word bucket (so "bath 3" → AE-3 before AE-30).
  const searchWholeRef = toRefQuery(searchRaw);
  const relevance = (c) => {
    if (!searchTokens.length) return 0;
    const ref = (c.caseRef || '').toLowerCase();
    if (ref === searchWholeRef)         return 0;  // exact whole-query ref
    if (ref.startsWith(searchWholeRef)) return 1;  // prefix
    if (ref.includes(searchWholeRef))   return 2;  // substring
    // Multi-word / field match — rank by how tightly a numeric word pins the ref.
    let best = 5;
    for (const t of searchTokens) {
      if (/^\d/.test(t)) {
        const tRef = `ae-${t}`;
        if (ref === tRef)            best = Math.min(best, 3);
        else if (ref.startsWith(tRef)) best = Math.min(best, 4);
      }
    }
    return best;
  };

  function compareCases(a, b) {
    if (searchTokens.length) {
      const ra = relevance(a), rb = relevance(b);
      if (ra !== rb) return ra - rb;
    }
    let av, bv;
    switch (sort.col) {
      case 'clinic':
        av = (a.clinic || '').toLowerCase();
        bv = (b.clinic || '').toLowerCase();
        break;
      case 'severity':
        av = SEVERITY_ORDER[a.severity] ?? 5;
        bv = SEVERITY_ORDER[b.severity] ?? 5;
        break;
      case 'cost':
        // Sum all currency totals as a single sort key. Mixed-currency cases are
        // summed nominally (no FX) — fine as a rough ranking.
        av = costTotal(a);
        bv = costTotal(b);
        break;
      case 'status':
        av = (a.status || '').toLowerCase();
        bv = (b.status || '').toLowerCase();
        break;
      case 'treatment':
        av = (a.treatment || '').toLowerCase();
        bv = (b.treatment || '').toLowerCase();
        break;
      case 'staffMember':
        av = caseStaffName(a);
        bv = caseStaffName(b);
        break;
      case 'incidentCategory':
        // Blanks sort after every real value (ascending), so A→Z starts with data.
        av = caseIncidentCategory(a).text.toLowerCase() || '\uffff';
        bv = caseIncidentCategory(b).text.toLowerCase() || '\uffff';
        break;
      case 'lastActivity': {
        const ta = lastActivityOf(a), tb = lastActivityOf(b);
        av = ta ? new Date(ta).getTime() : 0;
        bv = tb ? new Date(tb).getTime() : 0;
        break;
      }
      case 'sla':
        // Ascending = most urgent first (overdue, counting down, snoozed, held, none).
        av = slaSortKey(a, nowMs);
        bv = slaSortKey(b, nowMs);
        break;
      case 'reportedDate':
      default:
        av = a.reportedDate ? new Date(a.reportedDate).getTime() : 0;
        bv = b.reportedDate ? new Date(b.reportedDate).getTime() : 0;
        break;
    }
    if (av < bv) return sort.dir === 'asc' ? -1 : 1;
    if (av > bv) return sort.dir === 'asc' ?  1 : -1;
    return 0;
  }

  const sortedCases = [...filteredCases].sort(compareCases);

  const totalFiltered = sortedCases.length;
  // "Selected" count = live cases the filters (+ search) pick, whichever view
  // is showing. The bin loads ONLY deleted rows, so while it is open the live
  // counts are remembered from the last live render (or the server total if we
  // deep-linked straight into the bin) and Selected / All are the way back out.
  const selectedCount = sideView
    ? null
    : (viewAll ? cases.filter(c => passesSearch(c) && passesFilters(c)).length : totalFiltered);
  const liveSelected = useRef(null);
  if (!sideView && !loadingCases && !loadingRest) liveSelected.current = selectedCount;
  // Numbers only once they are final: the table loads in two batches, so a live
  // count would climb in stages. All uses the server total (one cached call).
  const shownSelected = sideView ? liveSelected.current : (loadingRest ? null : selectedCount);
  const shownAll      = stats?.total ?? null;
  const inSelected = !sideView && !viewAll;
  const inAll      = !sideView && viewAll;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / PAGE_SIZE));
  const pagedCases = sortedCases.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // "You might be looking for" — when the active filters leave 0 results but the
  // search alone matches cases, surface those (outside the current filters) so a
  // valid case is never hidden behind a bare "0 results". Capped, no pagination.
  const fallbackCases = (searchTokens.length && sortedCases.length === 0)
    ? cases.filter(c => caseMatchesSearch(c, searchTokens)).sort(compareCases).slice(0, 25)
    : [];
  const showingFallback = fallbackCases.length > 0;
  const rowsToShow = sortedCases.length ? pagedCases : fallbackCases;

  function toggleStatus(key) {
    setFilterStatuses(prev => ({ ...prev, [key]: !prev[key] }));
    setPage(1);
  }
  function toggleTreatment(key) {
    setFilterTreatments(prev => ({ ...prev, [key]: !prev[key] }));
    setPage(1);
  }
  function toggleSeverity(key) {
    setFilterSeverities(prev => ({ ...prev, [key]: !prev[key] }));
    setPage(1);
  }

  function resetFilters() {
    const defaults = parseCaseListParams(new URLSearchParams(), config);
    setFilterStatuses(defaults.filterStatuses);
    setFilterTreatments(defaults.filterTreatments);
    setFilterSeverities(defaults.filterSeverities);
    setFilterCountries(defaults.filterCountries);
    setFilterClinics(new Set());
    setTagStates({});
    setSearchText('');
    setDateRange(defaults.dateRange);
    setCustomDateFrom('');
    setCustomDateTo('');
    setPage(1);
    setSort(defaults.sort);
    setShowDeleted(false);
    setInactiveDays(0);
    setViewAll(false);
    setManual({});
  }

  // KPI icon helpers
  // One <td> per visible column. A plain function (not a component) so the row
  // stays a flat <tr> of cells for the table's keyboard handling.
  function renderCell(c, field) {
    switch (field) {
      case 'clinic':
        // Every clinic is "Therapie Clinic <place>" — the column shows the place.
        return (
          <td key={field} className="clinic-col" title={c.clinic || undefined}>
            {c.clinic ? c.clinic.replace(/^Therapie Clinic\s*/i, '') || c.clinic : <span className="muted">—</span>}
          </td>
        );
      case 'treatment':
        return (
          <td key={field}>
            {c.eventType === 'NearMiss'
              ? <span className="badge event-type">Near miss</span>
              : c.eventType === 'ClinicIncident'
              ? <span className="badge event-type">Clinic incident</span>
              : <TreatmentBadge value={c.treatment} />}
          </td>
        );
      case 'severity':
        return <td key={field}><SeverityBadge value={c.severity} /></td>;
      case 'status':
        return <td key={field}><StatusBadge status={c.status} /></td>;
      case 'staffMember': {
        const name = c._raw?.StaffMember || c._raw?.StaffMemberConcerned;
        return (
          <td key={field}>
            <div className="cell-owner">
              {name
                ? <><span className="avatar-sm">{name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}</span>{name}</>
                : <span className="muted">—</span>}
            </div>
          </td>
        );
      }
      case 'incidentCategory': {
        const { text, isOther } = caseIncidentCategory(c);
        if (!text) return <td key={field}><span className="muted">—</span></td>;
        const clipped = clipText(text, INCIDENT_CATEGORY_CLIP);
        return (
          <td key={field} className="category-col" title={clipped !== text || isOther ? (isOther ? `Other: ${text}` : text) : undefined}>
            {isOther ? <em>{clipped}</em> : clipped}
          </td>
        );
      }
      case 'reportedDate':
        return <td key={field} className="date-col">{formatDate(c.reportedDate)}</td>;
      case 'lastActivity': {
        const at = lastActivityOf(c);
        const idle = daysSince(at, nowMs);
        return (
          <td key={field} className="date-col">
            {at
              ? <span className={`ago${idle >= 30 ? ' stale' : ''}`} title={`Last activity ${formatDateTime(at)}`}>{agoLabel(at, nowMs)}</span>
              : <span className="muted">—</span>}
          </td>
        );
      }
      case 'sla': {
        const cell = buildSlaCell(c, { now: nowMs, formatDate });
        return (
          <td key={field}>
            {cell
              ? <span className={`sla-pill ${cell.tone}`} title={cell.title}>{cell.text}</span>
              : <span className="muted">—</span>}
          </td>
        );
      }
      case 'cost':
        return (
          <td key={field}>
            {c.costs && Object.keys(c.costs).length > 0
              ? <CostChip costs={c.costs} size="sm" />
              : <span className="muted">—</span>}
          </td>
        );
      default:
        return <td key={field} />;
    }
  }

  const iconCircle = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
    </svg>
  );

  // Set exactly one status filter (used by both the KPI cards and the slim strip).
  const selectStatusOnly = (statusKey) => {
    setFilterStatuses({ new: false, open: false, pending: false, claims: false, closed: false, [statusKey]: true });
    setPage(1);
  };

  // Single source for both the full KPI cards and the collapsed summary strip.
  const STAT_CARDS = [
    { key: 'new',      statusKey: 'new',      label: 'New',                   colorClass: 'warn',   value: stats?.new },
    { key: 'open',     statusKey: 'open',     label: 'Open',                  colorClass: 'info',   value: stats?.open },
    { key: 'pending',  statusKey: 'pending',  label: 'Pending',               colorClass: 'brand',  value: stats?.pending },
    { key: 'claims',   statusKey: 'claims',   label: 'With Claims',       colorClass: 'danger', value: stats?.withClaims },
    { key: 'closed',   statusKey: 'closed',   label: 'Closed',                colorClass: '',       value: stats?.closed },
  ];

  return (
    <>
      {/* ───────── Page head ───────── */}
      <header className="page-head">
        <div>
          <div className="breadcrumb">
            <span>Overview</span>
            <span>›</span>
            <span>All Cases</span>
          </div>
          <div className="title-row">
            {isClinicUser ? (
              <h1>All Cases</h1>
            ) : (
              <SavedViews
                defaultLabel="All Cases"
                isCustomized={isViewCustomised}
                getCurrentView={getCurrentView}
                onApply={applySavedView}
                onResetDefault={resetToDefaultView}
                onSaved={markViewSaved}
                onCountChange={setSavedViewsCount}
              />
            )}
          </div>
          <div className="page-sub">
            {loadingCases
              ? 'Loading…'
              : showingFallback
              ? `0 match your filters — showing ${fallbackCases.length} you might be looking for`
              : searchQuery
              ? `${totalFiltered} result${totalFiltered === 1 ? '' : 's'} for “${searchQuery}”`
              : showTest
              ? `${cases.length} test-clinic case${cases.length === 1 ? '' : 's'} — not counted in stats or reports`
              : `Showing ${totalFiltered} of ${cases.length} cases${loadingRest ? ' — loading the rest…' : ''}`}
          </div>
        </div>
        <div className="head-actions">
          <button
            className={`btn ghost stats-toggle${statsCollapsed ? '' : ' active'}`}
            onClick={toggleStats}
            aria-pressed={!statsCollapsed}
            title={statsCollapsed ? 'Show quick filters' : 'Hide quick filters to expand the table'}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="20" x2="18" y2="10"/>
              <line x1="12" y1="20" x2="12" y2="4"/>
              <line x1="6" y1="20" x2="6" y2="14"/>
              {/* eye-off style slash: draws in when quick filters are hidden, retracts when shown */}
              <line
                x1="3" y1="3" x2="21" y2="21"
                style={{
                  strokeDasharray: 26,
                  strokeDashoffset: statsCollapsed ? 0 : 26,
                  transition: 'stroke-dashoffset .22s var(--ease)',
                }}
              />
            </svg>
            Quick filters
          </button>
          <button
            className="btn ghost head-pill"
            onClick={() => (searchQuery ? loadSearch(searchQuery) : loadCases())}
            disabled={loadingCases}
            title="Refresh cases"
            aria-label="Refresh cases"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              style={loadingCases ? { animation: 'spin 0.8s linear infinite' } : undefined}>
              <path d="M23 4v6h-6M1 20v-6h6"/>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
            </svg>
            {loadingCases ? 'Refreshing…' : 'Refresh'}
          </button>
          <ShareLinkButton className="btn ghost sm head-pill" />
          {canSubmit && (
            <button className="btn primary" onClick={() => navigate('/new-case')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 5v14M5 12h14"/>
              </svg>
              New Case
            </button>
          )}
        </div>
      </header>

      {/* ───────── Saved-views welcome (non-clinic, until first view) ───────── */}
      {showViewsWelcome && (
        <div className="views-welcome" role="note">
          <div className="views-welcome-ic" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
          </div>
          <div className="views-welcome-body">
            <strong>Save your views</strong>
            <p>Set the filters, columns and sort you like, then tap the ★ by the title to save it. Saved views are <em>private to you</em> — pick one any time from the ▾ next to “All Cases”, and mark one as your default.</p>
          </div>
          <button type="button" className="views-welcome-x" aria-label="Dismiss" onClick={dismissViewsWelcome}>×</button>
        </div>
      )}

      {/* ───────── KPI row (toggle hides it entirely to expand the table) ───────── */}
      {!statsCollapsed && (
        <KpiGrid>
          {STAT_CARDS.map(s => (
            <StatCard
              key={s.key}
              label={s.label}
              value={loadingStats ? null : s.value}
              loading={loadingStats}
              colorClass={s.colorClass}
              icon={iconCircle}
              onClick={() => selectStatusOnly(s.statusKey)}
            />
          ))}
        </KpiGrid>
      )}


      {/* ───────── Cases layout ───────── */}
      <div className="row cases-layout" style={{ '--cases-min-h': `${casesMinH}px` }}>
        {/* ── Filter sidebar ── */}
        <div className={`filter-sidebar-wrapper${filtersOpen ? ' open' : ''}`}>
        <div className="panel">
          <div className="panel-head">
            <div className="panel-title">Filters</div>
            <button className="btn ghost sm" onClick={resetFilters}>
              Reset
            </button>
          </div>
          <div className="panel-body" ref={filterBodyRef}>
            <div className="filter-panel">

              {/* Status */}
              {config.visibleFilterGroups.status && (
                <FilterGroup
                  id="status" title="Status" refFn={groupRef('status')}
                  collapsed={isGroupCollapsed('status')} onToggle={() => toggleGroup('status')}
                  activeCount={groupActive.status}
                  onClear={() => setFilterStatuses({ new: false, open: false, pending: false, claims: false, closed: false })}
                >
                  <div className="filter-options">
                    <label className="filter-option">
                      <input type="checkbox" checked={filterStatuses.new} onChange={() => toggleStatus('new')} />
                      New
                      <span className="count-chip">{cases.filter(c => c.status === 'New').length}</span>
                    </label>
                    <label className="filter-option">
                      <input type="checkbox" checked={filterStatuses.open} onChange={() => toggleStatus('open')} />
                      Open
                      <span className="count-chip">{cases.filter(c => c.status === 'Open').length}</span>
                    </label>
                    <label className="filter-option">
                      <input type="checkbox" checked={filterStatuses.pending} onChange={() => toggleStatus('pending')} />
                      Pending
                      <span className="count-chip">{cases.filter(c => c.status === 'Pending').length}</span>
                    </label>
                    <label className="filter-option">
                      <input type="checkbox" checked={filterStatuses.claims} onChange={() => toggleStatus('claims')} />
                      With Claims
                      <span className="count-chip">{cases.filter(c => c.status === 'With Claims').length}</span>
                    </label>
                    <label className="filter-option">
                      <input type="checkbox" checked={filterStatuses.closed} onChange={() => toggleStatus('closed')} />
                      Closed
                      <span className="count-chip">{cases.filter(c => c.status === 'Closed').length}</span>
                    </label>
                  </div>
                </FilterGroup>
              )}

              {/* Event */}
              {config.visibleFilterGroups.treatment && (
                <FilterGroup
                  id="event" title="Event" refFn={groupRef('event')}
                  collapsed={isGroupCollapsed('event')} onToggle={() => toggleGroup('event')}
                  activeCount={groupActive.event}
                >
                  <div className="filter-options">
                    <label className="filter-option">
                      <input type="checkbox" checked={filterTreatments.lhr} onChange={() => toggleTreatment('lhr')} />
                      LHR
                      <span className="count-chip">{eventCounts.lhr}</span>
                    </label>
                    <label className="filter-option">
                      <input type="checkbox" checked={filterTreatments.awi} onChange={() => toggleTreatment('awi')} />
                      AWI
                      <span className="count-chip">{eventCounts.awi}</span>
                    </label>
                    <label className="filter-option">
                      <input type="checkbox" checked={filterTreatments.nearMiss} onChange={() => toggleTreatment('nearMiss')} />
                      Near miss
                      <span className="count-chip">{eventCounts.nearMiss}</span>
                    </label>
                    <label className="filter-option">
                      <input type="checkbox" checked={filterTreatments.clinicIncident} onChange={() => toggleTreatment('clinicIncident')} />
                      Clinic incident
                      <span className="count-chip">{eventCounts.clinicIncident}</span>
                    </label>
                    <label className="filter-option">
                      <input type="checkbox" checked={filterTreatments.other} onChange={() => toggleTreatment('other')} />
                      Other treatments
                      <span className="count-chip">{eventCounts.other}</span>
                    </label>
                  </div>
                </FilterGroup>
              )}

              {/* Severity */}
              {config.visibleFilterGroups.severity && (
                <FilterGroup
                  id="severity" title="Severity" refFn={groupRef('severity')}
                  collapsed={isGroupCollapsed('severity')} onToggle={() => toggleGroup('severity')}
                  activeCount={groupActive.severity}
                  onClear={groupActive.severity > 0
                    ? () => { setFilterSeverities(prev => Object.fromEntries(Object.keys(prev).map(k => [k, false]))); setPage(1); }
                    : null}
                >
                  <div className="filter-options">
                    {config.severityCodes.map(sc => (
                      <label className="filter-option" key={sc.key}>
                        <input
                          type="checkbox"
                          checked={filterSeverities[sc.value] ?? false}
                          onChange={() => toggleSeverity(sc.value)}
                        />
                        {sc.label}
                        <span className="count-chip">{cases.filter(c => c.severity === sc.value).length}</span>
                      </label>
                    ))}
                    <label className="filter-option">
                      <input
                        type="checkbox"
                        checked={filterSeverities['(none)'] ?? false}
                        onChange={() => toggleSeverity('(none)')}
                      />
                      No code
                      <span className="count-chip">{cases.filter(c => !c.severity).length}</span>
                    </label>
                  </div>
                </FilterGroup>
              )}

              {/* Country — "+" opens the picker; picks come back as pills */}
              {showCountryGroup && (
                <FilterGroup
                  id="country" title="Country" refFn={groupRef('country')}
                  collapsed={isGroupCollapsed('country')} onToggle={() => toggleGroup('country')}
                  activeCount={groupActive.country}
                  onAdd={() => setPicker('country')}
                  onClear={anyCountryActive(filterCountries)
                    ? () => { setFilterCountries({ ...EMPTY_COUNTRY_FILTER }); setFilterClinics(new Set()); setPage(1); }
                    : null}
                >
                  <PillSet
                    items={COUNTRY_FILTERS.filter(f => filterCountries[f.key]).map(f => ({
                      id: f.key, variant: 'add', label: f.label, title: `Only cases in ${f.label}`,
                    }))}
                    onRemove={(key) => { setFilterCountries(prev => ({ ...prev, [key]: false })); setFilterClinics(new Set()); setPage(1); }}
                  />
                </FilterGroup>
              )}

              {/* Clinic — "+" opens the picker; picks come back as pills */}
              {showClinicGroup && (
                <FilterGroup
                  id="clinic" title="Clinic" refFn={groupRef('clinic')}
                  collapsed={isGroupCollapsed('clinic')} onToggle={() => toggleGroup('clinic')}
                  activeCount={filterClinics.size}
                  onAdd={() => setPicker('clinic')}
                  onClear={filterClinics.size > 0 ? () => { setFilterClinics(new Set()); setPage(1); } : null}
                >
                  <PillSet
                    items={[...filterClinics].sort().map(name => ({
                      id: name, variant: 'add',
                      label: name.replace(/^Therapie Clinic\s*/i, '') || name,
                      title: `Only cases at ${name}`,
                    }))}
                    onRemove={(name) => { const n = new Set(filterClinics); n.delete(name); setFilterClinics(n); setPage(1); }}
                  />
                </FilterGroup>
              )}

              {/* Tags */}
              {(sidebarTags.length > 0 || tagVocabError) && (
                <FilterGroup
                  id="tags" title="Tags" refFn={groupRef('tags')}
                  collapsed={isGroupCollapsed('tags')} onToggle={() => toggleGroup('tags')}
                  activeCount={activeTagCount}
                  onAdd={() => setPicker('tags')}
                  onClear={activeTagCount > 0 ? () => { setTagStates({}); setPage(1); } : null}
                >
                  {tagVocabError && (
                    <div className="filter-note warn" role="status">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                        <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                      </svg>
                      <span>
                        Couldn’t load the full tag list — showing tags on loaded cases.{' '}
                        <a href="#" onClick={(e) => { e.preventDefault(); reloadTagVocab(); }}>Retry</a>
                      </span>
                    </div>
                  )}
                  <PillSet
                    items={Object.entries(tagStates).filter(([, m]) => m).map(([slug, mode]) => {
                      const label = sidebarTags.find(t => t.slug === slug)?.label || slug;
                      return {
                        id: slug, variant: mode === 'has' ? 'add' : 'remove', label,
                        title: mode === 'has' ? `Only cases tagged “${label}”` : `Excluding cases tagged “${label}”`,
                      };
                    })}
                    onRemove={(slug) => { setTagStates(prev => { const n = { ...prev }; delete n[slug]; return n; }); setPage(1); }}
                  />
                </FilterGroup>
              )}

              {/* Inactive for — stale-case triage */}
              <FilterGroup
                id="inactive" title="Inactive for" refFn={groupRef('inactive')}
                collapsed={isGroupCollapsed('inactive')} onToggle={() => toggleGroup('inactive')}
                hint="Only cases with no comment, upload, edit or status change for at least this long"
                activeCount={inactiveDays > 0 ? 1 : 0}
                onAdd={() => setPicker('inactive')}
              >
                {inactiveDays > 0 && (
                  <PillSet
                    items={[{ id: 'inactive', variant: 'set', label: `${inactiveDays}+ days`, title: 'No activity for at least this long' }]}
                    onRemove={() => { setInactiveDays(0); setPage(1); }}
                  />
                )}
              </FilterGroup>

              {/* Date reported */}
              <FilterGroup
                id="date" title="Date range" refFn={groupRef('date')}
                collapsed={isGroupCollapsed('date')} onToggle={() => toggleGroup('date')}
                activeCount={dateRange !== 'All time' ? 1 : 0}
                onAdd={() => setPicker('date')}
              >
                {dateRange !== 'All time' && (
                  <PillSet
                    items={[{ id: 'date', variant: 'set', label: dateRangeChipLabel(dateRange, customDateFrom, customDateTo), title: 'Date reported' }]}
                    onRemove={() => { setDateRange('All time'); setCustomDateFrom(''); setCustomDateTo(''); setPage(1); }}
                  />
                )}
              </FilterGroup>

              {/* Picker dialogs (portalled) */}
              {picker === 'country' && (
                <CountryPickerModal
                  options={COUNTRY_FILTERS.map(f => ({ ...f, count: cases.filter(c => c.country === f.country).length }))}
                  value={filterCountries}
                  onOk={(next) => {
                    const changed = COUNTRY_FILTERS.some(f => !!next[f.key] !== !!filterCountries[f.key]);
                    setFilterCountries({ ...EMPTY_COUNTRY_FILTER, ...next });
                    if (changed) setFilterClinics(new Set());   // clinic picks are scoped by country
                    setPage(1); setPicker(null);
                  }}
                  onCancel={() => setPicker(null)} />
              )}
              {picker === 'clinic' && (
                <ClinicPickerModal clinics={sidebarClinics} value={filterClinics}
                  onOk={(set) => { setFilterClinics(new Set(set)); setPage(1); setPicker(null); }} onCancel={() => setPicker(null)} />
              )}
              {picker === 'tags' && (
                <TagPickerModal
                  tags={sidebarTags.map(t => ({ ...t, swatchClass: (!t.isSystem && t.colour) ? `tag-swatch${tagColourClass(t.colour)}` : null }))}
                  value={tagStates}
                  onOk={(st) => { setTagStates(st); setPage(1); setPicker(null); }} onCancel={() => setPicker(null)} />
              )}
              {picker === 'inactive' && (
                <InactivePickerModal value={inactiveDays}
                  onOk={(d) => { setInactiveDays(d); setPage(1); setPicker(null); }} onCancel={() => setPicker(null)} />
              )}
              {picker === 'date' && (
                <DateRangePickerModal value={{ range: dateRange, from: customDateFrom, to: customDateTo }}
                  onOk={({ range, from, to }) => { setDateRange(range); setCustomDateFrom(range === 'Custom range' ? from : ''); setCustomDateTo(range === 'Custom range' ? to : ''); setPage(1); setPicker(null); }}
                  onCancel={() => setPicker(null)} />
              )}

            </div>
          </div>
        </div>
        </div>

        {/* ── Cases table panel ── */}
        <div className="panel" ref={casesPanelRef}>
          {/* Filters bar */}
          <div className="filters">
            {/* Filter toggle — visible only on mobile via CSS */}
            <button
              className="btn ghost sm filter-toggle-btn"
              onClick={() => setFiltersOpen(o => !o)}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="4" y1="6" x2="20" y2="6"/>
                <line x1="8" y1="12" x2="16" y2="12"/>
                <line x1="11" y1="18" x2="13" y2="18"/>
              </svg>
              {filtersOpen ? 'Hide filters' : 'Filters'}
            </button>
            <div className="search">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/>
                <line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                placeholder="Search by case ID, name, phone, clinic, therapist…"
                value={searchText}
                onChange={e => { setSearchText(e.target.value); setPage(1); }}
              />
            </div>
            {/* Selected · n (readout of the current filters) | All · n | Deleted */}
            <span className="btn-group sm" role="group" aria-label="Which cases to show">
            <span
              className={`chip${inSelected ? ' active' : ''}`}
              style={inSelected ? { cursor: 'default' } : undefined}
              title="Cases your filters select"
              onClick={inSelected ? undefined : () => { setShowDeleted(false); setShowTest(false); setViewAll(false); setPage(1); }}
              aria-current={inSelected ? 'true' : undefined}
            >
              Selected{shownSelected != null ? ` · ${shownSelected}` : ''}
            </span>
            <span
              className={`chip${inAll ? ' active' : ''}`}
              style={inAll ? { cursor: 'default' } : undefined}
              title="Every case, filters set aside"
              onClick={inAll ? undefined : () => { setShowDeleted(false); setShowTest(false); setViewAll(true); setPage(1); }}
              aria-current={inAll ? 'true' : undefined}
            >
              All{shownAll != null ? ` · ${shownAll}` : ''}
            </span>
            {user?.isSuperAdmin && (
              <span
                className={`chip${showDeleted ? ' active' : ''}`}
                onClick={() => { setShowDeleted(d => !d); setShowTest(false); setPage(1); }}
                title="Soft-deleted cases — restorable for 90 days"
                style={showDeleted ? { background: 'rgba(220,38,38,0.08)', borderColor: 'var(--danger)', color: 'var(--danger)' } : { color: 'var(--danger)' }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                  strokeLinecap="round" strokeLinejoin="round" style={{ width: 11, height: 11 }}>
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                </svg>
                Deleted
              </span>
            )}
            {user?.isSuperAdmin && (
              <span
                className={`chip${showTest ? ' active' : ''}`}
                onClick={() => { setShowTest(t => !t); setShowDeleted(false); setPage(1); }}
                title="Only the cases filed against a test clinic — hidden from everyone else, and never counted in stats or reports"
                style={showTest ? { background: 'var(--warn-bg, #FDF1DF)', borderColor: 'var(--warn-border, #F4D8AC)', color: 'var(--warn-fg, #9A5A12)' } : { color: 'var(--warn-fg, #9A5A12)' }}
              >
                Test clinic
              </span>
            )}
            </span>
            <ColumnPicker columns={columnDefs} hidden={hiddenCols} onToggle={toggleColumn} onReset={resetColumns} />
          </div>

          {/* Loading state */}
          {loadingCases && (
            <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text-3)' }}>
              Loading cases…
            </div>
          )}

          {/* Error state */}
          {!loadingCases && casesError && (
            <div style={{ padding: '20px' }}>
              <div className="callout danger">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                <div><strong>Could not load cases</strong> — {casesError}</div>
              </div>
            </div>
          )}

          {/* Background fetch failed — first batch still shown below */}
          {!loadingCases && !casesError && restError && (
            <div style={{ padding: '12px 20px 0' }}>
              <div className="callout warn">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
                <div>
                  <strong>Showing the first {FIRST_BATCH} cases only</strong> — the rest failed to load ({restError}).{' '}
                  <a href="#" onClick={(e) => { e.preventDefault(); loadCases(); }}>Retry</a>
                </div>
              </div>
            </div>
          )}

          {/* "You might be looking for" — search matched cases that the active
              filters exclude; surfaced so we never show a bare 0. */}
          {!loadingCases && !casesError && showingFallback && (
            <div className="fallback-strip">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <div style={{ flex: 1 }}>
                <strong>No cases match your current filters.</strong> Showing {fallbackCases.length} you might be looking for — matching “{searchText.trim()}” outside the active filters.
              </div>
              <button className="btn ghost sm" onClick={resetFilters} style={{ flexShrink: 0 }}>Reset filters</button>
            </div>
          )}

          {/* Table */}
          {!loadingCases && !casesError && (
            <div className="table-scroll">
            <table aria-label="Adverse Events cases">
              <thead>
                <tr>
                  <th>Case ID</th>
                  {visibleCols.map(({ field, label }) => (
                    <th
                      key={field}
                      className="sortable"
                      tabIndex={0}
                      title={COLUMN_HINTS[field]}
                      onClick={() => toggleSort(field)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSort(field); }
                      }}
                    >
                      <span className="th-label">{label}{sortIndicator(field)}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rowsToShow.length === 0 ? (
                  <tr>
                    <td colSpan={visibleCols.length + 1} style={{ textAlign: 'center', color: 'var(--text-3)', padding: '40px' }}>
                      {searchTokens.length
                        ? `No cases match “${searchText.trim()}”.`
                        : 'No cases match the current filters.'}
                    </td>
                  </tr>
                ) : (
                  rowsToShow.map((c) => {
                    const openCase = () => navigate(`/cases/${c.id}`, {
                      state: {
                        ids: rowsToShow.map(sc => String(sc.id)),
                        index: rowsToShow.findIndex(sc => sc.id === c.id),
                      },
                    });
                    return (
                    <tr
                      key={c.id}
                      tabIndex={0}
                      onClick={openCase}
                      onKeyDown={(e) => {
                        // Only act on keys pressed on the row itself, not on a child cell
                        if (e.target !== e.currentTarget) return;
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openCase(); }
                      }}
                      title={`View case ${c.caseRef}`}
                    >
                      <td className="case-id">{c.caseRef || `#${c.id}`}</td>
                      {visibleCols.map(col => renderCell(c, col.field))}
                    </tr>
                    );
                  })
                )}
              </tbody>
            </table>
            </div>
          )}

          {/* Pagination */}
          {!loadingCases && !casesError && totalPages > 1 && (
            <div className="pagination">
              <span>
                Showing <strong style={{ color: 'var(--text)' }}>{(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalFiltered)}</strong> of <strong style={{ color: 'var(--text)' }}>{totalFiltered}</strong>
              </span>
              <div className="page-btns">
                <button className="page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
                {getPageItems(page, totalPages).map((item, i) =>
                  item === 'gap' ? (
                    <span key={`gap-${i}`} className="pager-ellipsis" aria-hidden="true">…</span>
                  ) : (
                    <button
                      key={item}
                      className={`page-btn${item === page ? ' active' : ''}`}
                      aria-current={item === page ? 'page' : undefined}
                      onClick={() => setPage(item)}
                    >
                      {item}
                    </button>
                  )
                )}
                <button className="page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
